import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, FileCheck2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MODE_LABEL = [["mock", "Testovací (mock – bez kľúčov)"], ["live", "Sandbox / ostrý (SAPI-SK kľúče)"]];

export default function IntegrationsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [ef, setEf] = useState({ mode: "mock", participant_id: "", sapi_client_id: "", sapi_client_secret: "", auto_send: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/integrations");
    setCfg(data);
    setEf((s) => ({ ...s, mode: data.efaktura.mode, participant_id: data.efaktura.participant_id, sapi_client_id: data.efaktura.sapi_client_id, auto_send: !!data.efaktura.auto_send }));
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { mode: ef.mode, participant_id: ef.participant_id, sapi_client_id: ef.sapi_client_id, auto_send: ef.auto_send };
      if (ef.sapi_client_secret) body.sapi_client_secret = ef.sapi_client_secret;
      const { data } = await api.put("/integrations/efaktura", body);
      setCfg(data); setEf((s) => ({ ...s, sapi_client_secret: "" }));
      toast.success("eFaktúra nastavenia uložené");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!isAdmin) return (
    <div className="space-y-3" data-testid="integrations-unavailable">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><FileCheck2 className="h-5 w-5 text-primary" /> eFaktúra (Peppol / SAPI-SK)</h3>
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-6 text-center">
        <div className="font-heading text-xl font-bold uppercase text-amber-400">Dočasne nedostupné</div>
        <p className="mt-2 text-sm text-muted-foreground">Modul elektronickej fakturácie (eFaktúra / Peppol) je momentálne v testovacom režime a bude sprístupnený neskôr.</p>
      </div>
    </div>
  );
  if (!cfg) return <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="integrations-panel">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><FileCheck2 className="h-5 w-5 text-primary" /> eFaktúra (Peppol / SAPI-SK)</h3>
      <p className="text-sm text-muted-foreground">Odosielanie a príjem elektronických faktúr cez certifikovaného poskytovateľa <b>eFaktúra.sk</b> (štandard SAPI-SK). Testovací režim simuluje odoslanie bez kľúčov. Sandbox/ostré kľúče (client ID + secret) získate na <span className="whitespace-nowrap">developers.efaktura.sk</span>.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Režim</Label>
          <Select value={ef.mode} onValueChange={(v) => setEf((s) => ({ ...s, mode: v }))}>
            <SelectTrigger data-testid="ef-mode"><SelectValue /></SelectTrigger>
            <SelectContent>{MODE_LABEL.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Peppol identifikátor (X-Peppol-Participant-Id)</Label>
          <Input value={ef.participant_id} onChange={(e) => setEf((s) => ({ ...s, participant_id: e.target.value }))} placeholder="0245:1234567890" data-testid="ef-participant" />
        </div>
        <div className="space-y-1.5">
          <Label>SAPI-SK Client ID</Label>
          <Input value={ef.sapi_client_id} onChange={(e) => setEf((s) => ({ ...s, sapi_client_id: e.target.value }))} placeholder="efk_sapi_test_…" data-testid="ef-sapi-id" />
        </div>
        <div className="space-y-1.5">
          <Label>SAPI-SK Client Secret {cfg.efaktura.configured && <span className="text-xs text-emerald-500">(uložený)</span>}</Label>
          <Input type="password" value={ef.sapi_client_secret} onChange={(e) => setEf((s) => ({ ...s, sapi_client_secret: e.target.value }))} placeholder={cfg.efaktura.configured ? "•••••••• (ponechať)" : "efk_sapi_secret_…"} data-testid="ef-sapi-secret" />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Odoslanie cez Peppol spustíte na konkrétnej ostrej faktúre tlačidlom „Odoslať cez Peppol". Pre sandbox test použite identifikátor <code>0245:1234567890</code>.</p>
      <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background/40 p-3" data-testid="ef-autosend-row">
        <div>
          <div className="text-sm font-medium">Automaticky odoslať cez Peppol po vystavení</div>
          <div className="text-xs text-muted-foreground">Každá nová ostrá faktúra sa hneď odošle príjemcovi cez Peppol (bez ručného kliknutia).</div>
        </div>
        <Switch checked={ef.auto_send} onCheckedChange={(v) => setEf((s) => ({ ...s, auto_send: !!v }))} data-testid="ef-autosend" />
      </div>
      <Button onClick={save} disabled={saving} className="min-h-[44px]" data-testid="ef-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť eFaktúra"}</Button>
    </div>
  );
}
